// Mosqify API Service untuk PWA
// Mengganti Supabase dengan Mosqify API

import config from '../config/appConfig';
import { authStorage } from '../utils/authStorage';
import { tokenManager } from '../utils/tokenManager';
import { getLocalDateString } from '../utils/dateUtils';
import { captureEvent, getCurrentRoute } from '../posthogClient';

const API_BASE_URL = config.api.baseUrl;
const DEFAULT_REQUEST_TIMEOUT_MS = 10000;
const RETRYABLE_GET_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 250;

// API NewsType enum -> Indonesian label shown to jamaah
const NEWS_TYPE_LABELS = {
  general: 'Umum',
  religious: 'Keagamaan',
  community: 'Komunitas',
  education: 'Pendidikan',
  charity: 'Sosial',
  event: 'Acara',
  update: 'Info',
  other: 'Lainnya',
};

const API_ERROR_CODES = {
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  NETWORK_FETCH_FAILED: 'NETWORK_FETCH_FAILED',
  HTTP_ERROR: 'HTTP_ERROR',
};
const SLOW_REQUEST_THRESHOLD_MS = 1500;
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_PREFIX = 'mosqify_api_cache_v1:';
const CACHEABLE_ENDPOINT_PATTERNS = [/^\/prayer\/times/, /^\/mosques/, /^\/news/];

/**
 * Extract error message from API response. API may return message as array ["msg"] or string "msg".
 * Using message?.[0] when message is string incorrectly returns first char only (e.g. "S" from "Silakan").
 */
const getErrorMessage = (data, fallback) => {
  const m = data?.message;
  if (Array.isArray(m) && m.length > 0) return m[0];
  if (typeof m === 'string') return m;
  return fallback;
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const buildApiError = (code, message, meta = {}) => {
  const err = new Error(message);
  err.name = 'ApiRequestError';
  err.code = code;
  err.isApiError = true;
  Object.assign(err, meta);
  return err;
};

// Log API base URL saat service di-initialize

class MosqifyApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = null;
    this.refreshInProgress = false;
    this.responseCache = new Map();
    
    // Log API URL yang digunakan
    console.log('[MosqifyApiService] Initialized with API URL:', this.baseURL);
    
    // Warning jika masih menggunakan localhost
    if (this.baseURL.includes('localhost')) {
      console.warn('[MosqifyApiService] ⚠️ Using localhost API - make sure backend is running on http://localhost:3001');
    } else {
      console.log('[MosqifyApiService] ✅ Using production API:', this.baseURL);
    }

    // Initialize token from storage
    this.initToken();

    // Setup token refresh listener
    tokenManager.onTokenRefresh((newToken) => {
      this.token = newToken;
    });
  }

  // Initialize token from storage
  async initToken() {
    try {
      this.token = await authStorage.getToken();
    } catch (error) {
      console.warn('[MosqifyApiService] Failed to init token:', error);
    }
  }

  // Set authentication token
  async setToken(token, expiresIn = null) {
    this.token = token;
    await authStorage.setToken(token, expiresIn);
  }

  // Clear authentication token
  async clearToken() {
    this.token = null;
    await authStorage.removeToken();
  }

  // Get headers with authentication
  async getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Get fresh token from storage
    const token = this.token || await authStorage.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Generic API request method with token refresh
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Check if token needs refresh before request
    const token = this.token || await authStorage.getToken();
    if (token && await tokenManager.needsRefresh() && !this.refreshInProgress) {
      try {
        await this.refreshToken();
      } catch (error) {
        // If refresh fails, continue with current token
        // The response interceptor will handle 401 errors
        console.warn('[MosqifyApiService] Token refresh failed, continuing with current token:', error);
      }
    }

    const headers = await this.getHeaders();
    const timeoutMs = Number(options.timeoutMs) > 0 ? Number(options.timeoutMs) : DEFAULT_REQUEST_TIMEOUT_MS;
    const method = (options.method || 'GET').toUpperCase();
    const cachePolicy = options.cachePolicy || null;
    const maxAttempts = method === 'GET' ? (RETRYABLE_GET_ATTEMPTS + 1) : 1;
    const config = {
      headers,
      ...options,
    };
    delete config.timeoutMs;
    delete config.cachePolicy;

    const cacheKey = this.buildCacheKey(endpoint);
    const canUseReadCache = method === 'GET' && this.isCacheEligible(endpoint, cachePolicy);
    if (canUseReadCache) {
      const warmCache = this.getCachedResponse(cacheKey, cachePolicy?.maxAgeMs || DEFAULT_CACHE_TTL_MS);
      if (warmCache && (typeof navigator !== 'undefined' && navigator.onLine === false)) {
        captureEvent('api_cache_stale_served', {
          endpoint,
          reason: 'offline_prefetch',
          route: getCurrentRoute(),
        });
        return warmCache;
      }
    }

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const startTime = Date.now();
        const response = await fetch(url, { ...config, signal: controller.signal });
        clearTimeout(timeoutId);
        const elapsedMs = Date.now() - startTime;
        if (elapsedMs >= SLOW_REQUEST_THRESHOLD_MS) {
          captureEvent('api_request_slow', {
            endpoint,
            method,
            elapsed_ms: elapsedMs,
            route: getCurrentRoute(),
            status: response.status,
          });
        }
      
        // Handle unauthorized (401) - token expired or not authenticated
        if (response.status === 401) {
          const errorData = await response.json().catch(() => ({}));
        
          // The API marks every 401 with requiresLogin, so it cannot tell an expired
          // token from an anonymous call. If a token was sent, try one refresh first.
          const currentToken = this.token || await authStorage.getToken();
          const isAuthEndpoint = endpoint.startsWith('/auth/') || endpoint.startsWith('/mobile/auth/');
          if (currentToken && !this.refreshInProgress && !isAuthEndpoint) {
            try {
              await this.refreshToken();
            } catch (refreshError) {
              // Refresh failed: the session is over, tell AuthContext to clear it
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new Event('auth:expired'));
              }
              refreshError.requiresLogin = true;
              throw refreshError;
            }
            // Retry request with new token
            config.headers = await this.getHeaders();
            const retryResponse = await fetch(url, { ...config, signal: controller.signal });

            if (!retryResponse.ok) {
              const retryErrorData = await retryResponse.json().catch(() => ({}));
              // Create error object dengan response data untuk di-handle di component
              const error = new Error(getErrorMessage(retryErrorData, `HTTP ${retryResponse.status}: ${retryResponse.statusText}`));
              error.response = { data: retryErrorData, status: retryResponse.status };

              // Check requiresLogin in retry response - set flag tapi tidak redirect
              if (retryErrorData.requiresLogin || retryErrorData.response?.requiresLogin) {
                error.requiresLogin = true;
              }

              throw error;
            }

            return await retryResponse.json();
          }

          // No token (anonymous) or refresh already running - set flag, do not redirect
          const error = new Error(getErrorMessage(errorData, 'Silakan login terlebih dahulu untuk mengakses.'));
          error.response = { data: errorData, status: response.status };
          error.requiresLogin = true;
          throw error;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Create error object dengan response data untuk di-handle di component
          const error = buildApiError(
            API_ERROR_CODES.HTTP_ERROR,
            getErrorMessage(errorData, `HTTP ${response.status}: ${response.statusText}`),
            { response: { data: errorData, status: response.status } }
          );
        
          // Check requiresLogin for other errors too - set flag tapi tidak redirect
          if (errorData.requiresLogin || errorData.response?.requiresLogin) {
            error.requiresLogin = true;
          }
          throw error;
        }

        const data = await response.json();
        if (canUseReadCache) {
          this.setCachedResponse(cacheKey, data);
        }
        return data;
      } catch (error) {
        clearTimeout(timeoutId);
        const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
        const isAbort = error?.name === 'AbortError';
        const isFetchFailure = error?.name === 'TypeError' && (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch'));
      
        if (isAbort) {
          if (attempt < maxAttempts) {
            const jitter = Math.floor(Math.random() * 120);
            await sleep((RETRY_BASE_DELAY_MS * (2 ** (attempt - 1))) + jitter);
            continue;
          }
          const timeoutError = buildApiError(API_ERROR_CODES.NETWORK_TIMEOUT, 'Permintaan timeout. Silakan coba lagi.', {
            endpoint,
            url,
            timeoutMs,
            originalError: error,
            isNetworkError: true,
          });
          captureEvent('api_request_failed', {
            endpoint,
            method,
            code: timeoutError.code,
            route: getCurrentRoute(),
          });
          if (canUseReadCache) {
            const stale = this.getCachedResponse(cacheKey, cachePolicy?.staleMaxAgeMs || Number.MAX_SAFE_INTEGER);
            if (stale) {
              captureEvent('api_cache_stale_served', {
                endpoint,
                reason: 'timeout',
                route: getCurrentRoute(),
              });
              return stale;
            }
          }
          throw timeoutError;
        }
      
        if (isOffline) {
          const offlineError = buildApiError(API_ERROR_CODES.NETWORK_OFFLINE, 'Tidak ada koneksi internet.', {
            endpoint,
            url,
            originalError: error,
            isNetworkError: true,
          });
          captureEvent('api_request_failed', {
            endpoint,
            method,
            code: offlineError.code,
            route: getCurrentRoute(),
          });
          if (canUseReadCache) {
            const stale = this.getCachedResponse(cacheKey, cachePolicy?.staleMaxAgeMs || Number.MAX_SAFE_INTEGER);
            if (stale) {
              captureEvent('api_cache_stale_served', {
                endpoint,
                reason: 'offline',
                route: getCurrentRoute(),
              });
              return stale;
            }
          }
          throw offlineError;
        }

        if (isFetchFailure) {
          if (attempt < maxAttempts) {
            const jitter = Math.floor(Math.random() * 120);
            await sleep((RETRY_BASE_DELAY_MS * (2 ** (attempt - 1))) + jitter);
            continue;
          }
          const isLocalhost = this.baseURL.includes('localhost') || this.baseURL.includes('127.0.0.1');
          let errorMessage = 'NetworkError when attempting to fetch resource. ';
          if (isLocalhost) {
            errorMessage += 'API server mungkin tidak berjalan di localhost:3001. Pastikan backend API sudah dijalankan dengan `npm run start:dev` di folder mosqify-api.';
          } else {
            errorMessage += 'Periksa koneksi internet atau URL API.';
          }
          const fetchError = buildApiError(API_ERROR_CODES.NETWORK_FETCH_FAILED, errorMessage, {
            endpoint,
            url,
            originalError: error,
            isNetworkError: true,
          });
          captureEvent('api_request_failed', {
            endpoint,
            method,
            code: fetchError.code,
            route: getCurrentRoute(),
          });
          if (canUseReadCache) {
            const stale = this.getCachedResponse(cacheKey, cachePolicy?.staleMaxAgeMs || Number.MAX_SAFE_INTEGER);
            if (stale) {
              captureEvent('api_cache_stale_served', {
                endpoint,
                reason: 'fetch_failed',
                route: getCurrentRoute(),
              });
              return stale;
            }
          }
          throw fetchError;
        }

        if (error.name === 'SyntaxError' && error.message.includes('JSON')) {
          const jsonError = new Error('Gagal memparsing response dari server. Data mungkin tidak valid.');
          jsonError.name = 'JSONParseError';
          jsonError.originalError = error;
          throw jsonError;
        }

        throw error;
      }
    }
    throw buildApiError(API_ERROR_CODES.NETWORK_FETCH_FAILED, 'Permintaan gagal diproses.', { endpoint, url });
  }

  // GET request
  async get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    const response = await this.request(url, {
      method: 'GET',
      cachePolicy: {
        maxAgeMs: DEFAULT_CACHE_TTL_MS,
        staleMaxAgeMs: 24 * 60 * 60 * 1000,
      },
    });
    
    // Transform announcement detail responses for PWA compatibility
    // Check if this is a single announcement detail endpoint (not list)
    if (endpoint.includes('/news/') && !endpoint.includes('/news?') && !endpoint.endsWith('/news')) {
      // Single announcement detail - transform the response
      if (response && response.data) {
        return {
          ...response,
          data: this.transformAnnouncementForPWA(response.data)
        };
      }
      // Handle direct response (no wrapper)
      if (response && !response.data && response.id) {
        return this.transformAnnouncementForPWA(response);
      }
    }
    
    return response;
  }

  // POST request
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // PATCH request
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Authentication methods - Menggunakan /auth endpoint (mobile endpoint belum tersedia di production)
  async login(email, password) {
    try {
      // Try mobile endpoint first, fallback to regular auth
      let response;
      try {
        response = await this.post('/mobile/auth/login', { email, password });
      } catch (mobileError) {
        // If mobile endpoint not found (404), fallback to regular auth
        if (mobileError.response?.status === 404) {
          response = await this.post('/auth/login', { email, password });
        } else {
          throw mobileError;
        }
      }
      
      // Handle wrapped response format: { success: true, data: { access_token, user } }
      // or direct format: { access_token, user }
      const authData = response.data || response;
      
      // Handle both response formats: access_token (mobile) or accessToken (regular)
      const token = authData.access_token || authData.accessToken;
      const expiresIn = authData.expires_in || null;
      const user = authData.user || null;
      
      if (token) {
        await this.setToken(token, expiresIn);
      }
      
      // Normalize response format for consistency
      return {
        access_token: token,
        user: user,
        expires_in: expiresIn,
        token_type: authData.token_type || 'Bearer',
      };
    } catch (error) {
      throw error;
    }
  }

  async register(userData) {
    try {
      // Try mobile endpoint first, fallback to regular auth
      let response;
      try {
        response = await this.post('/mobile/auth/register', userData);
      } catch (mobileError) {
        // If mobile endpoint not found (404), fallback to regular auth
        if (mobileError.response?.status === 404) {
          response = await this.post('/auth/register', userData);
        } else {
          throw mobileError;
        }
      }
      
      // Handle wrapped response format: { success: true, data: { access_token, user } }
      // or direct format: { access_token, user }
      const authData = response.data || response;
      
      // Handle both response formats: access_token (mobile) or accessToken (regular)
      const token = authData.access_token || authData.accessToken;
      const expiresIn = authData.expires_in || null;
      const user = authData.user || null;
      
      if (token) {
        await this.setToken(token, expiresIn);
      }
      
      // Normalize response format for consistency
      return {
        access_token: token,
        user: user,
        expires_in: expiresIn,
        token_type: authData.token_type || 'Bearer',
        requiresVerification: authData.requiresVerification || false,
        email: authData.email || userData.email,
      };
    } catch (error) {
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      return await this.get('/auth/me');
    } catch (error) {
      throw error;
    }
  }

  async refreshToken() {
    // Prevent concurrent refresh attempts
    if (this.refreshInProgress) {
      // Wait for ongoing refresh
      while (this.refreshInProgress) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.token;
    }

    this.refreshInProgress = true;
    try {
      // Get current token for refresh request
      const currentToken = this.token || await authStorage.getToken();
      if (!currentToken) {
        throw new Error('No token available for refresh');
      }

      // Temporarily set token for refresh request
      const originalToken = this.token;
      this.token = currentToken;

      // Try mobile endpoint first, fallback to regular auth
      let response;
      try {
        response = await this.post('/mobile/auth/refresh', {});
      } catch (mobileError) {
        // If mobile endpoint not found (404), fallback to regular auth
        if (mobileError.response?.status === 404 || mobileError.response?.status === 401) {
          response = await this.post('/auth/refresh', {});
        } else {
          throw mobileError;
        }
      }
      
      // Handle different response formats
      const token = response.access_token || response.accessToken || (response.data && response.data.access_token);
      const expiresIn = response.expires_in || (response.data && response.data.expires_in) || null;
      
      if (token) {
        await this.setToken(token, expiresIn);
        this.token = token;
        return token;
      }
      throw new Error('No access token received from refresh');
    } catch (error) {
      // Clear token on refresh failure
      await this.clearToken();
      throw error;
    } finally {
      this.refreshInProgress = false;
    }
  }

  async logout() {
    try {
      // Try mobile endpoint first, fallback to regular auth
      try {
        await this.post('/mobile/auth/logout');
      } catch (mobileError) {
        // If mobile endpoint not found (404), fallback to regular auth
        if (mobileError.response?.status === 404) {
          await this.post('/auth/logout');
        } else {
          throw mobileError;
        }
      }
    } catch (error) {
    } finally {
      this.clearToken();
    }
  }

  // Delete account (soft-delete)
  async deleteAccount({ password, confirmation } = {}) {
    return this.post('/mobile/auth/delete-account', { password, confirmation });
  }

  // Google OAuth authentication
  async googleAuth(idToken) {
    try {
      if (!idToken) {
        throw new Error('Google ID token is required');
      }

      console.log('[MosqifyApiService] Attempting Google OAuth with mobile endpoint...');
      // Try mobile endpoint first, fallback to regular auth
      let response;
      try {
        response = await this.post('/mobile/auth/google', { idToken });
        console.log('[MosqifyApiService] Mobile endpoint success');
      } catch (mobileError) {
        console.log('[MosqifyApiService] Mobile endpoint failed, trying regular endpoint...', {
          status: mobileError.response?.status,
          message: mobileError.message
        });
        // If mobile endpoint not found (404), fallback to regular auth
        if (mobileError.response?.status === 404) {
          try {
            response = await this.post('/auth/google', { idToken });
            console.log('[MosqifyApiService] Regular endpoint success');
          } catch (regularError) {
            console.error('[MosqifyApiService] Both endpoints failed:', {
              mobile: mobileError.response?.status,
              regular: regularError.response?.status
            });
            const errorMsg = regularError.response?.data?.message || 
                          regularError.message || 
                          'Google authentication failed';
            const error = new Error(errorMsg);
            error.response = regularError.response;
            throw error;
          }
        } else {
          // Re-throw with better error message
          const errorMsg = mobileError.response?.data?.message || 
                          mobileError.message || 
                          'Google authentication failed';
          const error = new Error(errorMsg);
          error.response = mobileError.response;
          throw error;
        }
      }
      
      // Handle response format - check if wrapped in data property
      const responseData = response.data || response;
      console.log('[MosqifyApiService] Response data structure:', {
        hasData: !!response.data,
        hasAccessToken: !!(responseData.access_token || responseData.accessToken),
        hasUser: !!responseData.user,
        keys: Object.keys(responseData)
      });
      
      // Handle both response formats: access_token (mobile) or accessToken (regular)
      const token = responseData.access_token || responseData.accessToken;
      const expiresIn = responseData.expires_in || null;
      
      if (!token) {
        console.error('[MosqifyApiService] No token in response:', responseData);
        throw new Error('No access token received from Google authentication');
      }
      
      if (token) {
        await this.setToken(token, expiresIn);
      }
      
      // Normalize response format for consistency
      const normalizedResponse = {
        access_token: token || responseData.access_token,
        user: responseData.user || responseData.userData || null,
        expires_in: expiresIn || responseData.expires_in,
        token_type: responseData.token_type || 'Bearer',
      };
      
      console.log('[MosqifyApiService] Normalized response:', {
        hasToken: !!normalizedResponse.access_token,
        hasUser: !!normalizedResponse.user,
        userEmail: normalizedResponse.user?.email
      });
      
      return normalizedResponse;
    } catch (error) {
      console.error('[MosqifyApiService] Google OAuth error:', error);
      // Preserve original error with response data
      if (error.response) {
        const errorMsg = error.response?.data?.message || 
                        error.response?.data?.error ||
                        error.message || 
                        'Google authentication failed';
        const newError = new Error(errorMsg);
        newError.response = error.response;
        throw newError;
      }
      throw error;
    }
  }

  // Mosque methods
  async getMosqueProfile(mosqueId = null) {
    try {
      // If mosqueId is provided, fetch that specific mosque (now public)
      if (mosqueId) {
        const mosque = await this.get(`/mosques/${mosqueId}`);
        return mosque?.data || mosque || null;
      }
      // Otherwise, get first mosque (fallback) - endpoint /mosques is public
      const mosques = await this.get('/mosques', { limit: 1, page: 1 });
      return mosques.data?.mosques?.[0] ?? mosques.data?.[0] ?? null;
    } catch (error) {
      throw error;
    }
  }

  // Public mosque registration (no auth required)
  async submitMosque(data) {
    return this.post('/public/mosques/submit', data);
  }

  async getMosques(params = {}) {
    try {
      // Use public endpoint /mosques (list) - no auth required
      return await this.get('/mosques', params);
    } catch (error) {
      throw error;
    }
  }

  async getMosqueById(id) {
    try {
      return await this.get(`/mosques/${id}`);
    } catch (error) {
      throw error;
    }
  }

  // Campaign methods (for displaying donation campaigns)
  // Note: Campaigns endpoint requires authentication
  async getCampaigns(params = {}) {
    try {
      // Get campaigns - auth required
      return await this.get('/campaigns', params);
    } catch (error) {
      throw error;
    }
  }

  async getCampaignById(id, skipViewIncrement = false) {
    try {
      // Get campaign by ID - auth required
      const params = skipViewIncrement ? { skipViewIncrement: 'true' } : {};
      return await this.get(`/campaigns/${id}`, params);
    } catch (error) {
      throw error;
    }
  }

  /** Fetch donations for a campaign (for recent donors list). Auth required. */
  async getCampaignDonations(campaignId, params = { page: 1, limit: 10 }) {
    try {
      return await this.get(`/campaigns/${campaignId}/donations`, params);
    } catch (error) {
      throw error;
    }
  }

  /** Increment campaign share count. Auth required. */
  async shareCampaign(campaignId) {
    try {
      return await this.post(`/campaigns/${campaignId}/share`);
    } catch (error) {
      throw error;
    }
  }

  // Donation methods (for creating donations)
  async getDonations(params = {}) {
    try {
      return await this.get('/donations', params);
    } catch (error) {
      throw error;
    }
  }

  async createDonation(donationData) {
    try {
      return await this.post('/donations', donationData);
    } catch (error) {
      throw error;
    }
  }

  async getDonationById(id) {
    try {
      return await this.get(`/donations/${id}`);
    } catch (error) {
      throw error;
    }
  }

  async getMyDonations(params = {}) {
    try {
      return await this.get('/donations/my/donations', params);
    } catch (error) {
      throw error;
    }
  }

  // Zakat Calculator API methods
  async calculateZakatMal(payload) {
    return await this.post('/zakat/calculate/mal', payload);
  }

  async calculateZakatFitrah(payload) {
    return await this.post('/zakat/calculate/fitrah', payload);
  }

  async calculateZakatProfesi(payload) {
    return await this.post('/zakat/calculate/profesi', payload);
  }

  async getZakatCalculations(params = { page: 1, limit: 20 }) {
    return await this.get('/zakat/calculations', params);
  }

  async createZakatPayment(payload) {
    return await this.post('/zakat/payment', payload);
  }

  // Cash Flow methods
  async getCashFlowSummary(params = {}) {
    try {
      // Use statistics endpoint for summary
      return await this.get('/cash-flow/statistics', params);
    } catch (error) {
      throw error;
    }
  }

  async createCashFlow(cashFlowData) {
    try {
      return await this.post('/cash-flow', cashFlowData);
    } catch (error) {
      throw error;
    }
  }

  // Activities methods
  async getActivities(params = {}) {
    try {
      return await this.get('/activities', params);
    } catch (error) {
      throw error;
    }
  }

  async createActivity(activityData) {
    try {
      return await this.post('/activities', activityData);
    } catch (error) {
      throw error;
    }
  }

  async updateActivity(id, activityData) {
    try {
      return await this.patch(`/activities/${id}`, activityData);
    } catch (error) {
      throw error;
    }
  }

  // Register for activity (join as participant)
  async registerForActivity(id) {
    try {
      return await this.post(`/activities/${id}/register`);
    } catch (error) {
      throw error;
    }
  }

  async deleteActivity(id) {
    try {
      return await this.delete(`/activities/${id}`);
    } catch (error) {
      throw error;
    }
  }

  async getActivityById(id) {
    try {
      return await this.get(`/activities/${id}`);
    } catch (error) {
      throw error;
    }
  }

  // Events methods (event & ticket booking)
  async getEvents(params = {}) {
    try {
      const res = await this.get('/events/pwa', params);
      const data = res?.data ?? res;
      return { events: data?.events ?? [], total: data?.total ?? 0, page: data?.page ?? 1, limit: data?.limit ?? 10 };
    } catch (error) {
      throw error;
    }
  }

  async getEventById(id) {
    try {
      const res = await this.get(`/events/${id}`);
      return res?.data ?? res;
    } catch (error) {
      throw error;
    }
  }

  async registerForEvent(eventId, eventTicketId, quantity = 1) {
    try {
      return await this.post(`/events/${eventId}/register`, { eventTicketId, quantity });
    } catch (error) {
      throw error;
    }
  }

  async getMyEventRegistrations(params = {}) {
    try {
      const res = await this.get('/events/my-registrations', params);
      const data = res?.data ?? res;
      return { registrations: data?.registrations ?? [], total: data?.total ?? 0, page: data?.page ?? 1, limit: data?.limit ?? 20 };
    } catch (error) {
      throw error;
    }
  }

  // Payment - create Xendit payment request (order, donation, or event_registration)
  async createPaymentRequest(payload) {
    try {
      return await this.post('/payments/create-payment-request', payload);
    } catch (error) {
      throw error;
    }
  }

  // Helper function to transform announcement data from API format to PWA format
  transformAnnouncementForPWA(announcement) {
    if (!announcement) return null;
    
    // If already transformed, return as is
    if (announcement.judul) return announcement;
    
    // Get image URL - handle both image and imageUrl fields, ensure HTTPS for Cloudinary
    let imageUrl = announcement.image || announcement.imageUrl || announcement.gambar_url || null;
    if (imageUrl && imageUrl.startsWith('http://') && imageUrl.includes('cloudinary.com')) {
      imageUrl = imageUrl.replace('http://', 'https://');
    }
    
    // Get creator name from various possible fields
    const creatorName = announcement.creator?.name 
      || announcement.creator?.fullName
      || announcement.updater?.name
      || announcement.updater?.fullName
      || announcement.penulis
      || 'Admin';
    
    // Get date - prefer publishedAt, fallback to createdAt
    const dateValue = announcement.publishedAt 
      || announcement.createdAt 
      || announcement.created_at 
      || new Date().toISOString();
    
    return {
      ...announcement,
      // Map API fields to PWA expected fields
      judul: announcement.title || announcement.judul || '',
      isi: announcement.content || announcement.isi || '',
      gambar_url: imageUrl,
      kategori: NEWS_TYPE_LABELS[announcement.type] || announcement.kategori || announcement.type || 'Umum',
      penulis: creatorName,
      created_at: dateValue,
      ringkasan: announcement.summary || announcement.ringkasan || null,
      // Keep original fields for backward compatibility
      id: announcement.id,
      viewCount: announcement.viewCount || 0,
      clickCount: announcement.clickCount || 0,
      status: announcement.status,
      priority: announcement.priority,
      isPinned: announcement.isPinned || false,
      isPublic: announcement.isPublic !== false,
      tags: announcement.tags,
      expireAt: announcement.expireAt,
      publishAt: announcement.publishAt,
      // Also keep image field for fallback
      image: imageUrl,
    };
  }

  // Transform array of announcements
  transformAnnouncementsForPWA(announcements) {
    if (!announcements) return [];
    if (Array.isArray(announcements)) {
      return announcements.map(ann => this.transformAnnouncementForPWA(ann));
    }
    // Handle paginated response
    if (announcements.data && Array.isArray(announcements.data)) {
      return {
        ...announcements,
        data: announcements.data.map(ann => this.transformAnnouncementForPWA(ann))
      };
    }
    return announcements;
  }

  // Announcements methods
  async getAnnouncements(params = {}) {
    try {
      // For PWA, only fetch published announcements by default
      const pwaParams = {
        ...params,
        status: params.status || 'published', // Default to published for PWA
      };
      
      const response = await this.get('/news', pwaParams);
      
      // Transform response data for PWA compatibility
      if (response && response.data) {
        // Handle paginated response
        if (response.data.data && Array.isArray(response.data.data)) {
          // Filter and transform announcements
          const transformedData = response.data.data
            .filter(ann => ann.status === 'published' || !ann.status) // Only published or no status filter
            .map(ann => this.transformAnnouncementForPWA(ann));
          
          return {
            ...response,
            data: {
              ...response.data,
              data: transformedData,
              // Keep the server's pagination untouched (status=published is already requested)
              pagination: response.data.pagination || {
                total: transformedData.length,
                page: params.page || 1,
                limit: params.limit || 10,
                totalPages: Math.ceil(transformedData.length / (params.limit || 10))
              }
            }
          };
        }
        // Handle direct array response
        if (Array.isArray(response.data)) {
          const transformedData = response.data
            .filter(ann => ann.status === 'published' || !ann.status)
            .map(ann => this.transformAnnouncementForPWA(ann));
          
          return {
            ...response,
            data: transformedData
          };
        }
        // Handle single object
        if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
          // Only return if published
          if (response.data.status === 'published' || !response.data.status) {
            return {
              ...response,
              data: this.transformAnnouncementForPWA(response.data)
            };
          }
          // Return empty if not published
          return {
            ...response,
            data: null
          };
        }
      }
      
      // Handle direct array response (no wrapper)
      if (Array.isArray(response)) {
        return response
          .filter(ann => ann.status === 'published' || !ann.status)
          .map(ann => this.transformAnnouncementForPWA(ann));
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createAnnouncement(announcementData) {
    try {
      return await this.post('/news', announcementData);
    } catch (error) {
      throw error;
    }
  }

  // Products methods (Marketplace)
  async getProducts(params = {}) {
    try {
      return await this.get('/products', params);
    } catch (error) {
      throw error;
    }
  }

  async getProductById(id) {
    try {
      return await this.get(`/products/${id}`);
    } catch (error) {
      throw error;
    }
  }

  // Orders methods
  async createOrder(orderData) {
    try {
      return await this.post('/orders', orderData);
    } catch (error) {
      throw error;
    }
  }

  /** Checkout cart: create orders, returns { orderIds, totalAmount } for payment */
  async checkout(checkoutPayload) {
    try {
      const res = await this.post('/orders/checkout', checkoutPayload);
      const data = res?.data ?? res;
      return {
        orderIds: data?.orderIds ?? [],
        totalAmount: data?.totalAmount ?? 0,
        orderNumber: data?.orderNumber,
      };
    } catch (error) {
      throw error;
    }
  }

  async getOrders(params = {}) {
    try {
      return await this.get('/orders', params);
    } catch (error) {
      throw error;
    }
  }

  // Prayer Times methods
  async getPrayerTimes(params = {}) {
    try {
      return await this.get('/prayer/times', params);
    } catch (error) {
      throw error;
    }
  }

  async getPrayerTimesByMosque(mosqueId) {
    try {
      return await this.get(`/prayer/times/mosque/${mosqueId}`);
    } catch (error) {
      throw error;
    }
  }

  // Prayer Schedules methods (Jadwal Imam/Muazin)
  async getPrayerSchedules(mosqueId, params = {}) {
    try {
      return await this.get(`/prayer-schedules/${mosqueId}`, params);
    } catch (error) {
      throw error;
    }
  }

  // Get today's prayer schedule for a specific prayer type (user's local date)
  async getTodayPrayerSchedule(mosqueId, prayerType = null) {
    try {
      const today = getLocalDateString();
      const params = {
        startDate: today,
        endDate: today,
        limit: 10,
      };
      if (prayerType) {
        params.prayerType = prayerType;
      }
      const response = await this.get(`/prayer-schedules/${mosqueId}`, params);
      // Handle response format: { success: true, data: { schedules: [...] } }
      // or direct format: { schedules: [...] }
      if (response?.data?.schedules) {
        return response.data.schedules;
      }
      if (response?.schedules) {
        return response.schedules;
      }
      if (Array.isArray(response?.data)) {
        return response.data;
      }
      if (Array.isArray(response)) {
        return response;
      }
      return [];
    } catch (error) {
      // Don't throw - return empty array on error (schedule is optional)
      console.warn('[MosqifyApiService] Failed to fetch prayer schedules:', error);
      return [];
    }
  }

  // Health check
  async healthCheck() {
    try {
      return await this.get('/health');
    } catch (error) {
      throw error;
    }
  }

  // Upload payment proof (Bukti Bayar/Donasi) - matches DKM implementation
  async uploadPaymentProof(file, mosqueId = null, folder = 'donations') {
    try {
      // Build query parameters
      const params = [];
      if (mosqueId) params.push(`mosqueId=${mosqueId}`);
      if (folder) params.push(`folder=${folder}`);
      const queryString = params.length > 0 ? `?${params.join('&')}` : '';
      const url = `${this.baseURL}/financial/upload${queryString}`;
      
      // Get auth headers (but remove Content-Type for multipart)
      const headers = await this.getHeaders();
      delete headers['Content-Type']; // Let browser set Content-Type with boundary for multipart
      
      // Validate file before upload
      if (!file) {
        throw new Error('File tidak ditemukan');
      }
      
      // Validate file size (10MB max, matching backend)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Ukuran file terlalu besar. Maksimal 10MB');
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Tipe file tidak didukung. Hanya file gambar (JPEG, PNG, GIF, WEBP) dan PDF yang diizinkan');
      }
      
      // Create FormData
      const formData = new FormData();
      formData.append('file', file);
      
      // Make request with FormData
      const response = await fetch(url, {
        method: 'POST',
        headers: headers, // Authorization header only
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = getErrorMessage(errorData, `HTTP ${response.status}: ${response.statusText}`);
        const error = new Error(errorMessage);
        error.response = { data: errorData, status: response.status };
        throw error;
      }
      
      const data = await response.json();
      
      // Handle response format: { success: true, data: { url, secureUrl, publicId, ... } }
      // Match DKM response handling pattern
      if (data && data.success && data.data) {
        return data;
      } else if (data && (data.url || data.secureUrl)) {
        // Direct response format (fallback)
        return { success: true, data: data };
      } else {
        throw new Error('Format response tidak valid dari server');
      }
    } catch (error) {
      // Re-throw with better error message
      if (error.message) {
        throw error;
      }
      throw new Error(`Gagal mengupload file: ${error.message || 'Unknown error'}`);
    }
  }
  
  // Generic file upload method (for future use)
  async uploadFile(file, endpoint, mosqueId = null, folder = null) {
    try {
      let url = `${this.baseURL}${endpoint}`;
      const params = [];
      if (mosqueId) params.push(`mosqueId=${mosqueId}`);
      if (folder) params.push(`folder=${folder}`);
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      
      const headers = await this.getHeaders();
      delete headers['Content-Type'];
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(getErrorMessage(errorData, `HTTP ${response.status}: ${response.statusText}`));
        error.response = { data: errorData, status: response.status };
        throw error;
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  isCacheEligible(endpoint, cachePolicy) {
    if (cachePolicy?.enabled === false) return false;
    return CACHEABLE_ENDPOINT_PATTERNS.some((pattern) => pattern.test(endpoint));
  }

  buildCacheKey(endpoint) {
    return `${CACHE_PREFIX}${endpoint}`;
  }

  getCachedResponse(cacheKey, maxAgeMs) {
    const now = Date.now();
    const fromMemory = this.responseCache.get(cacheKey);
    if (fromMemory && now - fromMemory.savedAt <= maxAgeMs) {
      return fromMemory.data;
    }

    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.savedAt || now - parsed.savedAt > maxAgeMs) {
        return null;
      }
      this.responseCache.set(cacheKey, parsed);
      return parsed.data;
    } catch (_error) {
      return null;
    }
  }

  setCachedResponse(cacheKey, data) {
    const payload = { data, savedAt: Date.now() };
    this.responseCache.set(cacheKey, payload);
    try {
      localStorage.setItem(cacheKey, JSON.stringify(payload));
    } catch (_error) {
      // Ignore storage quota failures.
    }
  }
}

// Create singleton instance
const mosqifyApiService = new MosqifyApiService();

export default mosqifyApiService;

